import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Evaluation, KpiResultPeriod } from '@masar/common/models';
import { EvaluationInputType } from '@masar/common/models/evaluations/evaluation-input-type.type';
import { EvaluateService } from '@masar/features/evaluate/services/evaluate.service';
import {
    EvaluationInstance,
    EvaluationType,
} from '@masar/features/evaluate/types';

@Component({
    selector: 'app-evaluation-instance-portal',
    templateUrl: './evaluation-instance-portal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class EvaluationInstancePortalComponent implements OnInit {
    @Input() public entityId?: string;

    @Input() public savedEntityId?: string;

    @Input() public itemId?: string;

    @Input() public type!: EvaluationType;

    @Input() public mode: 'default' | 'view' = 'default';

    @Input() public title?: string;

    @Input() public customAction?: {
        name: string;
        template: TemplateRef<any>;
        item: KpiResultPeriod;
    };

    @Input() public useGroupedProjectStandards = false;

    @Input() public hideNotes = false;

    @Output() public update = new EventEmitter<EvaluationInstance>();

    public evaluations: Evaluation[] = [];

    public activeInputType: EvaluationInputType | null = null;

    public initialEvaluationId?: string;

    public isLoading = true;

    public hasMixedInputTypes = false;

    public constructor(private readonly evaluateService: EvaluateService) {}

    public ngOnInit(): void {
        if (this.itemId) {
            this.resolveExistingInstanceMode();
            return;
        }

        this.resolveCreateMode();
    }

    public selectEvaluation(evaluation: Evaluation): void {
        this.initialEvaluationId = evaluation.id;
        this.activeInputType = this.resolveInputType(evaluation.inputType);
    }

    public showSelectionScreen(): void {
        this.activeInputType = null;
    }

    private resolveExistingInstanceMode(): void {
        if (!this.itemId) {
            this.isLoading = false;
            return;
        }

        this.evaluateService
            .getInstance(this.itemId, this.type)
            .subscribe(item => {
                this.initialEvaluationId = item.evaluationId;
                this.activeInputType = this.resolveInputType(item.inputType);
                this.isLoading = false;
            });
    }

    private resolveCreateMode(): void {
        this.evaluateService
            .listEvaluations(this.type, this.savedEntityId)
            .subscribe(items => {
                this.evaluations = items;

                const uniqueTypes = Array.from(
                    new Set(items.map(x => this.resolveInputType(x.inputType)))
                );

                this.hasMixedInputTypes = uniqueTypes.length > 1;

                if (!items.length) {
                    this.activeInputType = 'standard';
                    this.isLoading = false;
                    return;
                }

                if (this.hasMixedInputTypes) {
                    this.activeInputType = null;
                    this.isLoading = false;
                    return;
                }

                const selectedEvaluation =
                    items.find(x => x.isDefault) ?? items[0];

                this.initialEvaluationId = selectedEvaluation.id;
                this.activeInputType = this.resolveInputType(
                    selectedEvaluation.inputType
                );
                this.isLoading = false;
            });
    }

    private resolveInputType(
        inputType?: EvaluationInputType
    ): EvaluationInputType {
        return inputType === 'rubric' ? 'rubric' : 'standard';
    }
}
